import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Alert, Typography } from '@mui/material';
import api from '../../api';

/**
 * SettingsAuthModal (local password mode)
 *
 * - If no local password is set (localStorage.jugeboxe_admin_hash absent) the modal offers to set one.
 * - Otherwise asks for the password and compares its SHA-256 hex with the stored hash.
 * - On success sets sessionStorage.jugeboxe_auth = "1" so pages can show admin controls.
 *
 * Security note: client-side password is convenient but not a replacement for server-side auth.
 */

const STORAGE_KEY = 'jugeboxe_admin_hash';
const SESSION_FLAG = 'jugeboxe_auth';

async function sha256Hex(str) {
  const enc = new TextEncoder().encode(str);
  const hashBuffer = await crypto.subtle.digest('SHA-256', enc);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

export default function SettingsAuthModal({ open, onClose, onSuccess }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const hasStored = typeof localStorage !== 'undefined' && !!localStorage.getItem(STORAGE_KEY);

  const resetState = () => {
    setPassword('');
    setConfirm('');
    setError('');
    setLoading(false);
  };

  const handleClose = () => {
    resetState();
    onClose && onClose();
  };

  const handleSetPassword = async (e) => {
    e && e.preventDefault();
    setError('');
    if (!password || password.length < 4) {
      setError('Mot de passe trop court (min 4 caractères).');
      return;
    }
    if (password !== confirm) {
      setError('Les deux champs ne correspondent pas.');
      return;
    }
    setLoading(true);
    try {
      const hash = await sha256Hex(password);
      localStorage.setItem(STORAGE_KEY, hash);
      try { sessionStorage.setItem(SESSION_FLAG, '1'); } catch (e) {}
      setLoading(false);
      handleClose();
      onSuccess && onSuccess();
    } catch (err) {
      console.error(err);
      setError('Erreur lors de la création du mot de passe.');
      setLoading(false);
    }
  };

  const handleVerifyPassword = async (e) => {
    e && e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const storedHash = localStorage.getItem(STORAGE_KEY);
      if (!storedHash) {
        setError('Aucun mot de passe local trouvé ; définissez d’abord un mot de passe.');
        setLoading(false);
        return;
      }
      const hash = await sha256Hex(password);
      if (hash === storedHash) {
        try { sessionStorage.setItem(SESSION_FLAG, '1'); } catch (e) {}
        setLoading(false);
        handleClose();
        onSuccess && onSuccess();
        return;
      }

      // Optional: fallback to server verification if available
      try {
        // If backend exposes /users/verify-password that accepts { password } and the request will use current auth token
        const res = await api.post('/users/verify-password', { password });
        if (res && (res.status === 200 || res.status === 204)) {
          try { sessionStorage.setItem(SESSION_FLAG, '1'); } catch (e) {}
          setLoading(false);
          handleClose();
          onSuccess && onSuccess();
          return;
        }
      } catch (e) {
        // ignore fallback error
      }

      setError('Mot de passe incorrect.');
      setLoading(false);
    } catch (err) {
      console.error(err);
      setError('Erreur lors de la vérification.');
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>{hasStored ? 'Accès professeur (mot de passe)' : 'Définir mot de passe professeur'}</DialogTitle>
      <DialogContent>
        {!hasStored ? (
          <>
            <Typography sx={{ mb: 1 }}>Aucun mot de passe défini. Définissez un mot de passe local pour protéger l'accès aux paramètres.</Typography>
            <form id="setPwdForm" onSubmit={handleSetPassword}>
              <TextField
                label="Nouveau mot de passe"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                fullWidth
                margin="normal"
                required
                autoFocus
              />
              <TextField
                label="Confirmer"
                type="password"
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                fullWidth
                margin="normal"
                required
              />
            </form>
          </>
        ) : (
          <>
            <Typography sx={{ mb: 1 }}>Entrez le mot de passe défini localement pour accéder aux paramètres.</Typography>
            <form id="verifyPwdForm" onSubmit={handleVerifyPassword}>
              <TextField
                label="Mot de passe"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                fullWidth
                margin="normal"
                required
                autoFocus
              />
            </form>
            <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
              Si tu veux utiliser la vérification serveur, assure-toi d'être connecté et que le backend propose /users/verify-password.
            </Typography>
          </>
        )}
        {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Annuler</Button>
        {!hasStored ? (
          <Button type="submit" form="setPwdForm" variant="contained" disabled={loading}>
            {loading ? 'Création...' : 'Définir mot de passe'}
          </Button>
        ) : (
          <Button type="submit" form="verifyPwdForm" variant="contained" disabled={loading}>
            {loading ? 'Vérification...' : 'Valider'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}